import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./landing.css";
import { http, formatApiError } from "./api";
import { VidhyaMark } from "./components/VidhyaMark";
import VidhyaWordmark from "./components/VidhyaWordmark";

/* ───────── Brand mark — official uploaded wordmark ───────── */
const StarIcon = ({ s = 18 }) => <VidhyaMark size={s} />;
const Brand = ({ size = 60 }) => <VidhyaWordmark size={size} testId="landing-logo" />;

/* ───────── small icon set ───────── */
const I = {
  arrow: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M20 6 9 17l-5-5" /></svg>,
  x: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M18 6 6 18M6 6l12 12" /></svg>,
  board: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18M9 9v11" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6z" /><path d="m9 12 2 2 4-4" /></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 4h16v16H4z" /><path d="m4 7 8 5 8-5" /></svg>,
  tasks: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 11l3 3 8-8" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" /></svg>,
  award: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="6" /><path d="M8 14l-1 8 5-3 5 3-1-8" /></svg>,
  zap: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M13 2 3 14h7l-1 8 10-12h-7z" /></svg>,
  chev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>,
  calendar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>,
  doc: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 3h9l5 5v13H5z" /><path d="M14 3v5h5" /></svg>,
  alert: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m10.3 3.9-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3.1l-8-14a2 2 0 0 0-3.4 0z" /><path d="M12 9v4M12 17h.01" /></svg>,
};

const PROBLEMS = [
  { icon: "calendar", text: "Different deadlines for every single program" },
  { icon: "doc", text: "Essays, transcripts, recommendations and funding forms scattered everywhere" },
  { icon: "mail", text: "Professors who never reply on the first try" },
  { icon: "award", text: "Scholarships that are hard to find and easy to miss" },
  { icon: "alert", text: "One small mistake that can cost you a whole year" },
];

const FEATURES = [
  { icon: I.board, title: "Find programs that fit", body: "Browse live university pages, compare requirements and deadlines, and shortlist programs that match your profile and budget." },
  { icon: I.board, title: "Track every application in one board", body: "Move each school from research to applied to offer. Every card holds the full checklist, deadlines, notes and status." },
  { icon: I.mail, title: "Draft outreach that sounds human", body: "Find the right professor or admissions contact and generate a tailored email based on the program and your interests." },
  { icon: I.tasks, title: "Stay on top of what's next", body: "Get prioritised tasks built from your real deadlines, so you always know what to do first." },
  { icon: I.award, title: "Find funding before it blocks you", body: "Search scholarships and funding filtered by field, location and eligibility — no more random Google hunts." },
  { icon: I.zap, title: "Autofill the repetitive stuff", body: "Securely reuse your saved details across application portals, so you're not retyping the same info fifteen times." },
];

const TRUST = [
  { t: "Live university data", s: "Pulled from real pages, not a stale database." },
  { t: "Verified where it counts", s: "Deadlines and requirements confirmed from the source." },
  { t: "Your data, your control", s: "Documents and personal details stay yours." },
  { t: "No bluffing your chances", s: "Honest admit odds — never inflated to keep you happy." },
];

const FAQS = [
  { q: "Who is Vidhya for?", a: "Any student applying to university — doing it solo, with no counselor and no agent. Vidhya keeps every program, deadline, essay prompt, email and scholarship in one workspace." },
  { q: "Does Vidhya do the application for me?", a: "No. Vidhya helps you research programs, organise deadlines, draft outreach and find funding — but you stay in control of your application. It guides and structures; the decisions and the writing stay yours." },
  { q: "How does the funding help work?", a: "Vidhya searches scholarships and funding opportunities filtered by your field, location and eligibility, then helps you track deadlines and close your budget gap — so cost doesn't stop you." },
  { q: "Is my personal data safe?", a: "Your documents and personal details stay under your control. We never store passports, national IDs, bank details or portal passwords — you enter those in the portal yourself." },
  { q: "Do I need to pay to start?", a: "No. The free plan lets you build a target list, track a limited number of programs and manage tasks. Upgrade when you're ready for unlimited programs, fit & budget analysis, scholarship discovery and autofill." },
];

export default function Landing() {
  const [authOpen, setAuthOpen] = useState(false);
  const [initialMode, setInitialMode] = useState("signup");
  const [billing, setBilling] = useState("annual");
  const [openFaq, setOpenFaq] = useState(0);

  const openModal = (mode) => { setInitialMode(mode); setAuthOpen(true); };

  /* Reveal-on-scroll */
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".lp .reveal"));
    if (!("IntersectionObserver" in window)) { els.forEach((el) => el.classList.add("in")); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } });
    }, { threshold: 0.14, rootMargin: "0px 0px -8% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  /* Smooth-scroll for in-page anchors */
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute("href").slice(1);
      const el = id && document.getElementById(id);
      if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const priceAmount = billing === "annual" ? "$11" : "$14.99";
  const priceBilling = billing === "annual" ? "billed $132 annually · save $48" : "billed monthly · cancel anytime";

  return (
    <div className="lp" data-testid="landing-page">
      {/* NAV */}
      <nav data-testid="landing-nav">
        <div className="nav-inner">
          <Link to="/"><Brand /></Link>
          <ul className="nav-links">
            <li><a href="#features">Product</a></li>
            <li><a href="#how">How it works</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><a href="#faq">FAQ</a></li>
          </ul>
          <div className="nav-cta">
            <button type="button" className="nav-signin" onClick={() => openModal("signin")} data-testid="nav-signin-btn">Sign in</button>
            <button type="button" className="btn btn-primary" onClick={() => openModal("signup")} data-testid="nav-signup-btn">Start free</button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="hero">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-left">
              <h1 data-testid="hero-headline">Apply to every university <span className="hl">from one workspace.</span></h1>
              <p className="hero-sub">
                Vidhya's AI reads real university pages and builds a tracked plan for every school — fit, requirements, fees, deadlines. Everything you need to <b>apply yourself and choose a university that's actually right for you</b>.
              </p>
              <div className="hero-cta">
                <button type="button" className="btn btn-primary btn-lg" onClick={() => openModal("signup")} data-testid="hero-start-btn">Start free {I.arrow}</button>
                <a href="#how" className="btn btn-ghost btn-lg" data-testid="hero-how-btn">See it in action</a>
              </div>
              <div className="microtrust">
                <span>{I.check} Never gives fake data</span>
                <span>{I.check} Never stores critical information</span>
                <span>{I.check} Never bluffs your chances</span>
              </div>
            </div>
            <div className="hero-right"><HeroDemo /></div>
          </div>
        </div>
      </header>

      {/* PROBLEM */}
      <section className="problem">
        <div className="container">
          <div className="problem-grid">
            <div className="reveal">
              <span className="eyebrow">The wall</span>
              <h2>The wall every applicant hits.</h2>
              <p className="lead">When it's time to apply, the support vanishes. No counselor. No one checking your list. Just you, a dozen portals, and one cycle to get it right.</p>
              <div className="problem-kicker">Vidhya turns the chaos into <span className="accent">one workspace.</span></div>
            </div>
            <ul className="problem-list reveal">
              {PROBLEMS.map((p, i) => (
                <li key={i}><span className="ic">{I[p.icon]}</span>{p.text}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features" id="features">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">What you get</span>
            <h2>Everything you need to apply <span className="accent">without dropping the ball.</span></h2>
            <p>One clear promise — research, plan, manage, and apply to universities from a single workspace.</p>
          </div>
          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div className="feat reveal" key={i} data-testid={`feature-card-${i}`}>
                <div className="fi">{f.icon}</div>
                <h4>{f.title}</h4>
                <p>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — demo anchor + trust */}
      <section className="trust" id="how">
        <div className="container">
          <div className="trust-card reveal">
            <div className="glow" />
            <div className="inner">
              <span className="eyebrow">Built for accuracy. Built for control.</span>
              <h2>Honest by design — because your application is on the line.</h2>
              <div className="trust-grid">
                {TRUST.map((t, i) => (
                  <div className="trust-item" key={i}>
                    <span className="ti">{I.shield}</span>
                    <div><b>{t.t}</b><span>{t.s}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pricing" id="pricing">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">Pricing</span>
            <h2>A wasted cycle costs you a year.</h2>
            <p>Vidhya costs less than one textbook a month.</p>
          </div>

          <div className="billing-wrap reveal">
            <div className="billing-toggle" role="tablist" aria-label="Billing cycle">
              <button type="button" className={`toggle-btn ${billing === "monthly" ? "active" : ""}`} onClick={() => setBilling("monthly")} data-testid="billing-monthly">Monthly</button>
              <button type="button" className={`toggle-btn ${billing === "annual" ? "active" : ""}`} onClick={() => setBilling("annual")} data-testid="billing-annual">Annual <span className="save-badge">save $48</span></button>
            </div>
          </div>

          <div className="pricing-grid reveal">
            <div className="plan">
              <div className="plan-name">Free</div>
              <div className="plan-tagline">Start building your target list.</div>
              <div className="plan-price"><span className="plan-amount">$0</span><span className="plan-period">forever</span></div>
              <div className="plan-billing">No card required</div>
              <ul className="plan-features">
                <PlanFeature>Start building your target list</PlanFeature>
                <PlanFeature>Track a limited number of programs</PlanFeature>
                <PlanFeature>Basic task management</PlanFeature>
                <PlanFeature locked>Fit & budget gap analysis</PlanFeature>
                <PlanFeature locked>Scholarship discovery</PlanFeature>
                <PlanFeature locked>Outreach drafting & autofill</PlanFeature>
              </ul>
              <button type="button" className="btn btn-outline btn-lg" onClick={() => openModal("signup")} data-testid="plan-explorer-btn">Start free</button>
            </div>

            <div className="plan featured">
              <div className="plan-badge">For serious applicants</div>
              <div className="plan-name">Paid</div>
              <div className="plan-tagline">Run your entire application, end to end.</div>
              <div className="plan-price"><span className="plan-amount" data-testid="plan-amount">{priceAmount}</span><span className="plan-period">/month</span></div>
              <div className="plan-billing" data-testid="plan-billing">{priceBilling}</div>
              <ul className="plan-features">
                <PlanFeature>Unlimited programs and universities</PlanFeature>
                <PlanFeature>Fit analysis & budget gap analysis</PlanFeature>
                <PlanFeature>Full application workspace</PlanFeature>
                <PlanFeature>Scholarship discovery</PlanFeature>
                <PlanFeature>Outreach drafting</PlanFeature>
                <PlanFeature>Autofill repetitive data with the Vidhya extension</PlanFeature>
              </ul>
              <button type="button" className="btn btn-primary btn-lg" onClick={() => openModal("signup")} data-testid="plan-navigator-btn">Start free</button>
              <div className="plan-note">Cancel anytime · 7-day money-back</div>
            </div>
          </div>
          <div className="pricing-footer">Prices in USD · No hidden fees · Cancel anytime</div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq" id="faq">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">FAQ</span>
            <h2>Questions, answered <span className="accent">straight.</span></h2>
          </div>
          <div className="faq-list reveal">
            {FAQS.map((f, i) => (
              <div className={`faq-item ${openFaq === i ? "open" : ""}`} key={i} data-testid={`faq-item-${i}`}>
                <button type="button" className="faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`faq-q-${i}`}>
                  {f.q}<span className="chev">{I.chev}</span>
                </button>
                <div className="faq-a"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card reveal">
            <div className="glow" />
            <div className="cta-dev">विद्या</div>
            <div className="inner">
              <span className="eyebrow">Research. Plan. Apply. This cycle.</span>
              <h2>Stop applying alone. <span className="accent">Start getting in.</span></h2>
              <p>Start free and keep every application moving in one place. About 30 seconds to set up — no card, no catch.</p>
              <button type="button" className="btn btn-lime btn-lg" onClick={() => openModal("signup")} data-testid="cta-final-btn">Start free {I.arrow}</button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <Link to="/"><Brand size={46} /></Link>
              <p className="footer-tag">One workspace to research, plan, manage, and apply to universities.</p>
              <div className="footer-dev">विद्या</div>
              <div className="footer-dev-tag">— knowledge</div>
            </div>
            <div className="footer-col">
              <h4>Product</h4>
              <ul>
                <li><a href="#features">Features</a></li>
                <li><a href="#how">How it works</a></li>
                <li><a href="#pricing">Pricing</a></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                <li><a href="mailto:hello@govidhya.com">Contact</a></li>
                <li><a href="https://instagram.com/vidhyahq" target="_blank" rel="noopener noreferrer">Instagram</a></li>
                <li><button type="button" onClick={() => openModal("signup")}>Start free</button></li>
                <li><button type="button" onClick={() => openModal("signin")}>Sign in</button></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Resources</h4>
              <ul>
                <li><Link to="/privacy" data-testid="footer-privacy-link">Privacy</Link></li>
                <li><Link to="/terms" data-testid="footer-terms-link">Terms</Link></li>
                <li><a href="#pricing">Plans</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© {new Date().getFullYear()} Vidhya · All rights reserved.</span>
            <span className="footer-bottom-links">
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
            </span>
          </div>
        </div>
      </footer>

      {authOpen && <AuthModal initialMode={initialMode} onClose={() => setAuthOpen(false)} />}
    </div>
  );
}

/* ═══════════════════════ HERO DEMO ═══════════════════════ */
function HeroDemo() {
  return (
    <div className="demo" data-testid="demo-player">
      <div className="demo-window">
        <div className="demo-bar">
          <span className="demo-dot" /><span className="demo-dot" /><span className="demo-dot" />
          <span className="demo-url">app.vidhya.io · My Universities</span>
        </div>
        <div className="demo-stage">
          <div className="d-board">
            <div className="d-col">
              <div className="d-colh"><i style={{ background: "var(--cyan)" }} />Researching</div>
              <div className="d-card d-1"><b>ETH Zürich</b><span>MSc Computer Science</span>
                <div className="d-meta"><div className="d-pg"><i style={{ width: "20%" }} /></div><span className="d-due">Dec 15</span></div>
              </div>
            </div>
            <div className="d-col">
              <div className="d-colh"><i style={{ background: "var(--indigo)" }} />Applying</div>
              <div className="d-card d-2"><b>NUS</b><span>MComp AI</span>
                <div className="d-meta"><div className="d-pg"><i style={{ width: "55%" }} /></div><span className="d-due soon">4d</span></div>
              </div>
            </div>
            <div className="d-col">
              <div className="d-colh"><i style={{ background: "var(--verified)" }} />Offer</div>
              <div className="d-card d-3"><b>U of Toronto</b><span>MSc CS</span>
                <div className="d-meta"><div className="d-pg"><i style={{ width: "100%", background: "var(--verified)" }} /></div><span className="d-due" style={{ color: "var(--verified)" }}>Offer</span></div>
              </div>
            </div>
          </div>
          <div className="d-cop">
            <div className="glow" />
            <div className="d-cop-head"><span className="star"><StarIcon s={14} /></span>Ask Vidhya</div>
            <div className="d-cop-line">NUS tuition is <b style={{ color: "#fff" }}>S$17,550/yr</b> and the deadline is <b style={{ color: "#fff" }}>Dec 1</b>. You have a S$9,550 funding gap — I matched 4 scholarships that could close it.</div>
            <span className="d-cop-vbadge">{I.check} Verified · nus.edu.sg · Jun 18</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Pricing helper ─────────── */
function PlanFeature({ children, locked }) {
  return (
    <li className={locked ? "locked" : ""}>
      <span className={locked ? "lock" : "check"}>
        {locked ? (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12l5 5L20 7" /></svg>
        )}
      </span>
      {children}
    </li>
  );
}

/* ─────────── Auth modal (signup → verify → onboarding; signin → forgot reset) ─────────── */
function AuthModal({ onClose, initialMode = "signup" }) {
  const [mode, setMode] = useState(initialMode);
  const [pendingEmail, setPendingEmail] = useState("");
  return (
    <div className="lp-modal-shell" onClick={onClose} data-testid="auth-modal">
      <div className="lp-modal" onClick={(e) => e.stopPropagation()}>
        <button className="lp-modal-close" onClick={onClose} aria-label="Close" data-testid="auth-close">×</button>
        {mode === "signin" && <SignInForm switchMode={setMode} onForgot={(email) => { setPendingEmail(email); setMode("forgot"); }} />}
        {mode === "signup" && <SignUpForm switchMode={setMode} onAwaitVerification={(email) => { setPendingEmail(email); setMode("verify"); }} />}
        {mode === "verify" && <VerifyEmailForm email={pendingEmail} switchMode={setMode} />}
        {mode === "forgot" && <ForgotPasswordForm initialEmail={pendingEmail} switchMode={setMode} />}
      </div>
    </div>
  );
}

function ModeFooter({ children }) { return <div className="lp-mode-footer">{children}</div>; }

function SignInForm({ switchMode, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await http.post("/auth/login", { email, password });
      localStorage.setItem("sa_token", data.token);
      window.location.href = data.user && data.user.onboarded === false ? "/onboarding" : "/app";
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="eyebrow">Welcome back</div>
      <h3 className="lp-modal-h">Sign <em>in</em>.</h3>
      <p className="lp-modal-sub">Continue where you left off.</p>
      <form onSubmit={submit} className="lp-modal-form">
        <div className="lp-row">
          <label>Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="signin-email" />
        </div>
        <div className="lp-row">
          <div className="lp-row-head">
            <label>Password</label>
            <button type="button" className="lp-link-inline" onClick={() => onForgot(email)} data-testid="signin-forgot-link">Forgot?</button>
          </div>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="signin-password" />
        </div>
        {err && <div className="lp-error" data-testid="signin-error">{err}</div>}
        <button type="submit" className="lp-modal-submit" disabled={busy} data-testid="signin-submit">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      <ModeFooter>New here? <button type="button" onClick={() => switchMode("signup")} data-testid="switch-to-signup">Create account</button></ModeFooter>
    </>
  );
}

function SignUpForm({ switchMode, onAwaitVerification }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await http.post("/auth/register", { email, name, password });
      onAwaitVerification(email);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="eyebrow">Free to start</div>
      <h3 className="lp-modal-h">Create your <em>account</em>.</h3>
      <p className="lp-modal-sub">Sign up in seconds — we'll email a 6-digit code to confirm.</p>
      <form onSubmit={submit} className="lp-modal-form">
        <div className="lp-row"><label>Full name</label><input value={name} onChange={(e) => setName(e.target.value)} required data-testid="signup-name" /></div>
        <div className="lp-row"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="signup-email" /></div>
        <div className="lp-row"><label>Password (min 6)</label><input type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="signup-password" /></div>
        {err && <div className="lp-error" data-testid="signup-error">{err}</div>}
        <button type="submit" className="lp-modal-submit" disabled={busy} data-testid="signup-submit">{busy ? "Sending code…" : "Create account"}</button>
        <p className="lp-modal-legal" data-testid="signup-legal-note">
          By creating an account you agree to our <Link to="/terms" data-testid="signup-terms-link">Terms</Link> and <Link to="/privacy" data-testid="signup-privacy-link">Privacy Policy</Link>.
        </p>
      </form>
      <ModeFooter>Already have an account? <button type="button" onClick={() => switchMode("signin")} data-testid="switch-to-signin-from-signup">Sign in</button></ModeFooter>
    </>
  );
}

function VerifyEmailForm({ email, switchMode }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(`We sent a 6-digit code to ${email}. Enter it to finish setting up.`);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await http.post("/auth/verify-email", { email, code });
      localStorage.setItem("sa_token", data.token);
      window.location.href = data.user && data.user.onboarded === false ? "/onboarding" : "/app";
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  const resend = async () => {
    setErr(""); setInfo(""); setResending(true);
    try { await http.post("/auth/resend-verification", { email }); setInfo(`A fresh code is on the way to ${email}.`); }
    catch (e2) { setErr(formatApiError(e2)); } finally { setResending(false); }
  };

  return (
    <>
      <div className="eyebrow">One last step</div>
      <h3 className="lp-modal-h">Check your <em>inbox</em>.</h3>
      {info && <p className="lp-modal-sub" data-testid="verify-info">{info}</p>}
      <form onSubmit={submit} className="lp-modal-form">
        <div className="lp-row">
          <label>6-digit code</label>
          <input inputMode="numeric" maxLength={6} pattern="\d{6}" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required autoFocus data-testid="verify-code-input" className="lp-code-input" />
        </div>
        {err && <div className="lp-error" data-testid="verify-error">{err}</div>}
        <button type="submit" className="lp-modal-submit" disabled={busy || code.length !== 6} data-testid="verify-submit">{busy ? "Verifying…" : "Verify & continue"}</button>
      </form>
      <ModeFooter>
        <button type="button" onClick={() => switchMode("signup")} data-testid="verify-back">← Wrong email?</button>
        <button type="button" className="lp-mode-footer-spaced" onClick={resend} disabled={resending} data-testid="verify-resend">{resending ? "Sending…" : "Resend code"}</button>
      </ModeFooter>
    </>
  );
}

function ForgotPasswordForm({ initialEmail, switchMode }) {
  const [stage, setStage] = useState("request");
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setBusy(true);
    try { await http.post("/auth/forgot-password", { email }); setInfo(`If an account exists for ${email}, a reset code is on its way.`); setStage("reset"); }
    catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  const resetPwd = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setBusy(true);
    try {
      await http.post("/auth/reset-password", { email, code, new_password: newPwd });
      setInfo("Password updated. You can sign in now.");
      setTimeout(() => switchMode("signin"), 900);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="eyebrow">Forgot your password?</div>
      <h3 className="lp-modal-h">Reset your <em>password</em>.</h3>
      <p className="lp-modal-sub">{stage === "request" ? "Enter your email — we'll send a 6-digit reset code." : "Enter the code from your inbox and pick a new password."}</p>
      {info && <div className="lp-info" data-testid="forgot-info">{info}</div>}
      {err && <div className="lp-error" data-testid="forgot-error">{err}</div>}
      {stage === "request" ? (
        <form onSubmit={sendCode} className="lp-modal-form">
          <div className="lp-row"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="forgot-email" /></div>
          <button type="submit" className="lp-modal-submit" disabled={busy} data-testid="forgot-send-btn">{busy ? "Sending…" : "Send reset code"}</button>
        </form>
      ) : (
        <form onSubmit={resetPwd} className="lp-modal-form">
          <div className="lp-row"><label>6-digit code</label><input inputMode="numeric" maxLength={6} pattern="\d{6}" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required autoFocus data-testid="reset-code-input" className="lp-code-input" /></div>
          <div className="lp-row"><label>New password (min 6)</label><input type="password" minLength={6} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required data-testid="reset-new-password-input" /></div>
          <button type="submit" className="lp-modal-submit" disabled={busy || code.length !== 6} data-testid="reset-submit-btn">{busy ? "Updating…" : "Reset password"}</button>
        </form>
      )}
      <ModeFooter><button type="button" onClick={() => switchMode("signin")} data-testid="forgot-back-to-signin">← Back to sign in</button></ModeFooter>
    </>
  );
}
